import { useMemo, useSyncExternalStore } from 'react';
import { buildSeries, indexAtTime } from '../charts/series';
import { messages } from '../i18n/ja';
import type { PlaybackController } from '../playback/controller';
import type { ComputeSuccess } from '../worker/protocol';
import type { RideWarning } from '../safety/evaluate';
import { LineChart, type ChartBand } from './LineChart';

/**
 * 距離–速度・距離–高度・距離–上下 G のグラフ（FR-ANA-004）。再生位置を示す。
 * 再生位置は 0.1 秒刻みで更新する（HUD と同じ）。
 */
export function AnalysisCharts({
  ride,
  controller,
  warnings = [],
}: {
  readonly ride: ComputeSuccess | undefined;
  readonly controller: PlaybackController | undefined;
  /** 走行の評価。G の超過区間と失速地点を、グラフに印として示す */
  readonly warnings?: readonly RideWarning[] | undefined;
}) {
  const t = messages.charts;
  const series = useMemo(() => (ride ? buildSeries(ride.result.frames) : undefined), [ride]);
  const snapshot = useSyncExternalStore(
    controller?.subscribe ?? noopSubscribe,
    controller?.getSnapshot ?? emptySnapshot,
  );
  if (!series) return <p className="placeholder">{t.empty}</p>;
  const markerIndex = indexAtTime(series, snapshot.time);
  const stallMarks = warnings.filter((w) => w.kind === 'stall').map((w) => w.startS);
  const gBands: ChartBand[] = warnings
    .filter((w) => w.kind === 'vertical-g-high' || w.kind === 'vertical-g-low')
    .map((w) => ({
      from: w.startS,
      to: w.endS,
      level: w.level === 'caution' || w.level === 'warning' ? w.level : 'blocking',
    }));

  return (
    <div className="charts" data-testid="charts" aria-label={t.label} role="group">
      <LineChart
        title={t.speed}
        xLabel={t.distanceAxis}
        yLabel={t.speedAxis}
        xs={series.distance}
        ys={series.speedKmh}
        markerIndex={markerIndex}
        lineStyle="solid"
        marks={stallMarks}
        testId="chart-speed"
      />
      <LineChart
        title={t.altitude}
        xLabel={t.distanceAxis}
        yLabel={t.altitudeAxis}
        xs={series.distance}
        ys={series.altitude}
        markerIndex={markerIndex}
        lineStyle="dashed"
        marks={stallMarks}
        testId="chart-altitude"
      />
      <LineChart
        title={t.verticalG}
        xLabel={t.distanceAxis}
        yLabel={t.gAxis}
        xs={series.distance}
        ys={series.verticalG}
        markerIndex={markerIndex}
        lineStyle="dotted"
        referenceY={1}
        bands={gBands}
        marks={stallMarks}
        testId="chart-vertical-g"
      />
    </div>
  );
}

function noopSubscribe(): () => void {
  return () => undefined;
}

const emptySnapshot = (() => {
  const snapshot = { time: 0, duration: 0, playing: false, rate: 1 as const, ended: false };
  return () => snapshot;
})();
